import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useGlobalCatalogStore } from '@/stores/globalCatalogStore';
import { useCreateEstimateStore } from '@/stores/createEstimate';
import accountService from '@/services/accountService';
import productCatalogService from '@/services/productCatalogService';
import propertyReportsService from '@/services/propertyReportsService';
import { getToken } from '@/utilities/cookies';

export const usePropertyReportFormStore = defineStore('propertyReportForm', () => {
  const globalCatalogStore = useGlobalCatalogStore();
  const createEstimateStore = useCreateEstimateStore();

  const isInitializing = ref(false);
  const pricingLoading = ref(true);
  const freeTrialsLoading = ref(true);

  const currentUser = ref(null);
  const pricePerReport = ref(-2);
  const uomReport = ref('');
  const freeReportsRemaining = ref(0);
  const branchShipToMapping = ref({});

  const selectedBranchCodeRef = ref(null);
  const selectedShipToSequenceRef = ref(null);

  const catalogBranches = computed(() => 
    Array.isArray(globalCatalogStore.branches) ? globalCatalogStore.branches : []
  );

  const catalogJobAccounts = computed(() => 
    Array.isArray(globalCatalogStore.customerShipToAddresses) ? globalCatalogStore.customerShipToAddresses : []
  );

  const jobInfoBranches = computed(() => {
    const list = createEstimateStore.estimatorData?.jobInformation?.branchList;
    if (!Array.isArray(list)) return [];

    return list
      .map((branch) => ({
        BranchCode: branch?.BranchCode || branch?.value || '',
        BranchName: branch?.BranchName || branch?.label || ''
      }))
      .filter((branch) => branch.BranchCode && branch.BranchName);
  });

  const jobInfoJobAccounts = computed(() => {
    const list = createEstimateStore.estimatorData?.jobInformation?.jobAccounts;
    if (!Array.isArray(list)) return [];

    return list
      .map((account) => {
        const shipToSequenceId = account?.ShipToSequenceId ?? account?.value ?? null;
        const addressLine2 = account?.AddressLine2 || account?.label || '';

        return {
          ShipToSequenceId: shipToSequenceId,
          AddressLine2: addressLine2
        };
      })
      .filter((account) => account.ShipToSequenceId !== null);
  });

  const branches = computed(() => 
    catalogBranches.value.length ? catalogBranches.value : jobInfoBranches.value
  );

  const jobAccounts = computed(() => 
    catalogJobAccounts.value.length ? catalogJobAccounts.value : jobInfoJobAccounts.value
  );

  const selectedBranchCode = computed({
    get: () => {
      const stored = selectedBranchCodeRef.value;
      const jobInfoSelectedBranch = createEstimateStore.estimatorData?.jobInformation?.selectedBranch;
      return stored || jobInfoSelectedBranch || branches.value[0]?.BranchCode || null;
    },
    set: (value) => {
      selectedBranchCodeRef.value = value;
    }
  });

  const shipToAddresses = computed(() => {
    if (!selectedBranchCode.value || !Object.keys(branchShipToMapping.value).length) {
      return jobAccounts.value;
    }
    const branchShipToIds = branchShipToMapping.value[selectedBranchCode.value] || [];
    if (branchShipToIds.length === 0) {
      return jobAccounts.value;
    }
    return jobAccounts.value.filter(jobAccount =>
      branchShipToIds.includes(jobAccount.ShipToSequenceId)
    );
  });

  const selectedShipToSequence = computed({
    get: () => {
      const stored = selectedShipToSequenceRef.value;
      if (stored && shipToAddresses.value.find((ja) => String(ja.ShipToSequenceId) === String(stored))) {
        return stored;
      }

      const jobInfoSelectedJobAccount = createEstimateStore.estimatorData?.jobInformation?.jobAccount;
      if (jobInfoSelectedJobAccount && shipToAddresses.value.find((ja) => String(ja.ShipToSequenceId) === String(jobInfoSelectedJobAccount))) {
        return jobInfoSelectedJobAccount;
      }

      return shipToAddresses.value[0]?.ShipToSequenceId || null;
    },
    set: (value) => {
      selectedShipToSequenceRef.value = value;
    }
  });

  const selectedJobAccount = computed(() => 
    shipToAddresses.value.find(a => String(a.ShipToSequenceId) === String(selectedShipToSequence.value)) || null
  );

  const selectedJobAccountLabel = computed(() => {
    const account = selectedJobAccount.value;
    return account ? `#${account.ShipToSequenceId}: ${account.AddressLine2}` : 'Select Job Account';
  });

  const availableJobAccounts = computed(() => 
    shipToAddresses.value.filter(a => String(a.ShipToSequenceId) !== String(selectedShipToSequence.value))
  );

  async function initialize() {
    if (isInitializing.value) return;
    isInitializing.value = true;

    try {
      await globalCatalogStore.getEligibleBranches();
      await globalCatalogStore.fetchJobAccounts();
      await loadBranchShipToMapping();
      await loadCurrentUser();

      if (currentUser.value) {
        fetchFreeTrials();
        fetchPricing();
      } else {
        freeTrialsLoading.value = false;
        pricingLoading.value = false;
      }
    } catch (error) {
      console.error('Error initializing property report form:', error);
      freeTrialsLoading.value = false;
      pricingLoading.value = false;
    } finally {
      isInitializing.value = false;
    }
  }

  async function loadCurrentUser() {
    const res = await accountService.fetchUserDetails(getToken());
    if (res.success) {
      currentUser.value = res.data;
    }
  }

  async function loadBranchShipToMapping() {
    const response = await propertyReportsService.getBranchShipToMapping();
    if (response.success) {
      branchShipToMapping.value = response.data;
    } else {
      branchShipToMapping.value = {};
    }
  }

  async function fetchPricing() {
    if (!selectedBranchCode.value || !selectedShipToSequence.value) {
      pricingLoading.value = false;
      return;
    }

    const user = currentUser.value;
    if (!user?.CustomerDetail?.CustomerCode) {
      pricePerReport.value = -1;
      pricingLoading.value = false;
      return;
    }

    pricingLoading.value = true;

    const itemCodes = [{
      orderItemIndex: 0,
      ItemCode: 'PROPERTYREPORT',
      Quantity: 1,
      UOM: null,
      Option: ''
    }];

    const resp = await productCatalogService.getProductPricing({
      itemCodes,
      branchCode: selectedBranchCode.value,
      customerCode: user.CustomerDetail.CustomerCode,
      shipToSequenceNumber: selectedShipToSequence.value
    });

    if (resp.success) {
      const p = resp.data?.Data?.Pricing?.[0];
      if (p?.ErrorRetrievingPrice) {
        pricePerReport.value = -1;
      } else {
        pricePerReport.value = p?.Price ?? -1;
        uomReport.value = p?.UnitOfMeasure || '';
      }
    } else {
      pricePerReport.value = -1;
    }

    pricingLoading.value = false;
  }

  async function fetchFreeTrials() {
    const customerCode = currentUser.value?.CustomerDetail?.CustomerCode?.trim?.() || '';

    if (!customerCode) {
      freeReportsRemaining.value = 0;
      freeTrialsLoading.value = false;
      return;
    }

    freeTrialsLoading.value = true;

    const resp = await propertyReportsService.freeTrials(customerCode);
    if (resp.success) {
      freeReportsRemaining.value = resp.data.FreeTrialsRemaining;
    } else {
      freeReportsRemaining.value = 0;
    }

    freeTrialsLoading.value = false;
  }

  function setSelectedBranch(branchCode) {
    selectedBranchCodeRef.value = branchCode;
    fetchPricing();
  }

  function setSelectedJobAccount(shipToSequenceId) {
    selectedShipToSequenceRef.value = shipToSequenceId;
    fetchPricing();
  }

  function formatPrice(price) {
    if (price === null || price === undefined) return '0.00';
    return Number(price).toFixed(2);
  }

  function reset() {
    selectedBranchCodeRef.value = null;
    selectedShipToSequenceRef.value = null;
    pricePerReport.value = -2;
    uomReport.value = '';
    freeReportsRemaining.value = 0;
    pricingLoading.value = true;
    freeTrialsLoading.value = true;
  }

  return {
    isInitializing,
    pricingLoading,
    freeTrialsLoading,
    currentUser,
    pricePerReport,
    uomReport,
    freeReportsRemaining,
    branches,
    jobAccounts,
    shipToAddresses,
    selectedBranchCode,
    selectedShipToSequence,
    selectedJobAccount,
    selectedJobAccountLabel,
    availableJobAccounts,
    initialize,
    fetchPricing,
    fetchFreeTrials,
    setSelectedBranch,
    setSelectedJobAccount,
    formatPrice,
    reset
  };
});
